import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, ArrowRight, Users, Trophy, Calendar } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface Sprint {
  id: string;
  objective: string;
  status: string;
  created_at: string;
  completed_at?: string;
  inform_stage_completed: boolean;
  decide_stage_completed: boolean;
  execute_stage_completed: boolean;
  assess_stage_completed: boolean;
  sprint_contributions?: any[];
}

interface SprintDashboardProps {
  activeSprint?: Sprint | null;
  recentSprints: Sprint[];
  onCreateNew: () => void;
  onResumeSprint: (sprint: Sprint) => void;
  onViewSprint: (sprint: Sprint) => void;
  showCreateCard?: boolean;
}

export const SprintDashboard = ({
  activeSprint,
  recentSprints,
  onCreateNew,
  onResumeSprint,
  onViewSprint,
  showCreateCard = true,
}: SprintDashboardProps) => {
  const getStageProgress = (sprint: Sprint) => {
    const stages = [
      sprint.inform_stage_completed,
      sprint.decide_stage_completed,
      sprint.execute_stage_completed,
      sprint.assess_stage_completed,
    ];
    const completed = stages.filter(Boolean).length;
    return (completed / 4) * 100;
  };

  const getCurrentStage = (sprint: Sprint) => {
    if (!sprint.inform_stage_completed) return "Inform";
    if (!sprint.decide_stage_completed) return "Decide";
    if (!sprint.execute_stage_completed) return "Execute";
    if (!sprint.assess_stage_completed) return "Assess & Sustain";
    return "Completed";
  };

  return (
    <div className="space-y-6">
      {/* Active Sprint Section */}
      {activeSprint && (
        <Card className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-medium rounded">
                  In Progress
                </span>
                <span className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(activeSprint.created_at), { addSuffix: true })}
                </span>
              </div>
              <h3 className="text-xl font-semibold mb-2">{activeSprint.objective}</h3>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Stage: {getCurrentStage(activeSprint)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{activeSprint.sprint_contributions?.length || 0} contributions</span>
                </div>
              </div>
            </div>
            <Button onClick={() => onResumeSprint(activeSprint)} size="lg">
              Continue Sprint
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-muted-foreground mb-2">
              <span>Progress</span>
              <span>{Math.round(getStageProgress(activeSprint))}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${getStageProgress(activeSprint)}%` }}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Create New Sprint CTA */}
      {showCreateCard && (
        <Card className="p-8 text-center hover:shadow-lg transition-shadow cursor-pointer border-2 border-dashed" onClick={onCreateNew}>
          <div className="max-w-md mx-auto space-y-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
              <Plus className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-semibold">Launch a Sprint</h3>
            <p className="text-muted-foreground">
              Collaborate with your team to architect powerful prompts through our 4-stage process
            </p>
            <Button size="lg" className="mt-4">
              <Plus className="mr-2 h-4 w-4" />
              Create Sprint
            </Button>
          </div>
        </Card>
      )}

      {/* Recent Sprints */}
      {recentSprints.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-primary" />
            Recent Sprints
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentSprints.map((sprint) => (
              <Card 
                key={sprint.id} 
                className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onViewSprint(sprint)}
              >
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        sprint.status === 'completed' 
                          ? 'bg-green-500/20 text-green-700 dark:text-green-400'
                          : 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400'
                      }`}>
                        {sprint.status === 'completed' ? 'Completed' : 'Paused'}
                      </span>
                    </div>
                    <h4 className="font-medium line-clamp-2">{sprint.objective}</h4>
                  </div>
                  
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div className="flex items-center justify-between">
                      <span>Progress</span>
                      <span>{Math.round(getStageProgress(sprint))}%</span>
                    </div>
                    {sprint.completed_at && (
                      <div>
                        Completed {formatDistanceToNow(new Date(sprint.completed_at), { addSuffix: true })}
                      </div>
                    )}
                  </div>

                  <div className="h-1 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${getStageProgress(sprint)}%` }}
                    />
                  </div>

                  <Button variant="ghost" size="sm" className="w-full">
                    View Details
                    <ArrowRight className="ml-2 h-3 w-3" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
